import { clsx } from "clsx";
import { renderEmphasis } from "@/components/kit/lib/emphasis";
import { Stack } from "@/components/Stack/Stack";

export interface LeadMagnetCtaCopy {
  eyebrow: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  buttonLabel: string;
}

// Default resume-checklist copy, matching the locked design and the v1 default
// magnet (`resume-template-and-checklist`). A post promoting a different magnet
// resolves a different href; per-magnet copy is a later enhancement.
export const DEFAULT_CTA_BAND: LeadMagnetCtaCopy = {
  eyebrow: "Бесплатно",
  title: "Проверьте своё резюме по чек-листу",
  titleAccent: "резюме",
  subtitle:
    "Чек-лист покажет, что рекрутер ожидает увидеть в отклике, — пройдитесь по пунктам перед следующим откликом.",
  buttonLabel: "Забрать чек-лист",
};

export const DEFAULT_CTA_INLINE: LeadMagnetCtaCopy = {
  eyebrow: "Чек-лист",
  title: "Чек-лист: проверьте резюме за 10 минут",
  titleAccent: "",
  subtitle: "Пункты, по которым рекрутер просматривает отклик, — в одном файле.",
  buttonLabel: "Скачать чек-лист",
};

export const DEFAULT_CTA_RAIL: LeadMagnetCtaCopy = {
  eyebrow: "",
  title: "Чек-лист сильного резюме",
  titleAccent: "",
  subtitle: "Пункты, по которым рекрутер просматривает отклик.",
  buttonLabel: "Скачать",
};

/** The dark lead-magnet band closing the `/blog` index — lime accent + button. */
export function BlogCtaBand({
  href,
  copy = DEFAULT_CTA_BAND,
}: {
  href: string;
  copy?: LeadMagnetCtaCopy;
}) {
  return (
    <section
      className="blog-cta-band dark"
      data-surface="dark"
      data-component="BlogCtaBand"
      data-section="blog-cta"
    >
      <div className="blog-cta-band-inner">
        <p className="eyebrow">{copy.eyebrow}</p>
        <h2 className="blog-cta-band-title">{renderEmphasis(copy.title, copy.titleAccent)}</h2>
        <p className="blog-cta-band-sub">{copy.subtitle}</p>
        <a href={href} className="ds-btn ds-btn-action" data-track="blog-index.leadmagnet.cta">
          {copy.buttonLabel}
        </a>
      </div>
    </section>
  );
}

/** Inline lavender lead-magnet card inside the article body — purple button. */
export function BlogLeadMagnetCard({
  href,
  copy = DEFAULT_CTA_INLINE,
}: {
  href: string;
  copy?: LeadMagnetCtaCopy;
}) {
  return (
    <aside className="blog-lm-card" data-surface="light" data-component="BlogLeadMagnetCard">
      <Stack gap={8} className="blog-lm-card-text">
        <b className="blog-lm-card-title">{copy.title}</b>
        <span className="blog-lm-card-sub">{copy.subtitle}</span>
      </Stack>
      <a href={href} className="ds-btn ds-btn-action" data-track="blog-article.leadmagnet.inline">
        {copy.buttonLabel}
      </a>
    </aside>
  );
}

/** Small dark lead-magnet promo card in the article's sticky right rail. */
export function BlogRailPromo({
  href,
  copy = DEFAULT_CTA_RAIL,
  className,
}: {
  href: string;
  copy?: LeadMagnetCtaCopy;
  className?: string;
}) {
  return (
    <aside
      className={clsx("blog-rail-promo dark", className)}
      data-surface="dark"
      data-component="BlogRailPromo"
    >
      <b className="blog-rail-promo-title">{copy.title}</b>
      <p className="blog-rail-promo-sub">{copy.subtitle}</p>
      <a href={href} className="ds-btn ds-btn-action" data-track="blog-article.leadmagnet.rail">
        {copy.buttonLabel}
      </a>
    </aside>
  );
}
